import { InjectionToken } from '@angular/core';

export type LogLevel = 'DBUG' | 'INFO' | 'WARN' | 'FAIL' | 'NONE';

export type Constructor<T = {}> = new (...args: any[]) => T;

export interface ILogger<T = any> {
  debug(message: string, ...args: any[]): void;
  info(message: string, ...args: any[]): void;
  warn(message: string, ...args: any[]): void;
  error(message: string, ...args: any[]): void;
  fail(message: string, ...args: any[]): void;
  getCategoryName(): string;
  getCategoryType(): T;
}

/**
 * Short method interface extending ILogger with convenient method names
 * Provides d(), i(), w(), e(), f() methods for concise logging
 */
export interface IShortLogger<T = any> extends ILogger<T> {
  d(message: string, ...args: any[]): void; // debug
  i(message: string, ...args: any[]): void; // info
  w(message: string, ...args: any[]): void; // warn
  e(message: string, ...args: any[]): void; // error
  f(message: string, ...args: any[]): void; // fail
}

export interface LoggerConfig {
  logLevel?: LogLevel;
  format?: string;
  includeTimestamp?: boolean;
  includeCategory?: boolean;
}

export const LOGGER_CONFIG = new InjectionToken<LoggerConfig>('LOGGER_CONFIG');

/**
 * Logger implementation similar to C# ILogger<T> with short method support
 */
export class Logger<T> implements IShortLogger<T> {
  private categoryName: string;
  private categoryType: Constructor<T>;

  constructor(categoryType: Constructor<T>) {
    this.categoryName = categoryType.name.trim();
    if (!this.categoryName) {
      this.categoryName = 'Anonymous';
    }
    this.categoryType = categoryType;
  }

  private formatMessage(level: LogLevel, message: string): string {
    const format = '[{timestamp}] ({level}) {{category}}: {message}';
    return format
      .replace('{timestamp}', new Date().toISOString())
      .replace('{level}', level)
      .replace('{category}', `${this.categoryName}`)
      .replace('{message}', message);
  }

  debug(message: string, ...args: any[]): void {
    const formattedMessage = this.formatMessage('DBUG', message);
    console.debug(formattedMessage, ...args);
  }

  info(message: string, ...args: any[]): void {
    const formattedMessage = this.formatMessage('INFO', message);
    console.info(formattedMessage, ...args);
  }

  warn(message: string, ...args: any[]): void {
    const formattedMessage = this.formatMessage('WARN', message);
    console.warn(formattedMessage, ...args);
  }

  error(message: string, ...args: any[]): void {
    const formattedMessage = this.formatMessage('FAIL', message);
    console.error(formattedMessage, ...args);
  }

  fail(message: string, ...args: any[]): void {
    const formattedMessage = this.formatMessage('FAIL', message);
    console.error(formattedMessage, ...args);
  }

  // Short method implementations
  d(message: string, ...args: any[]): void {
    this.debug(message, ...args);
  }

  i(message: string, ...args: any[]): void {
    this.info(message, ...args);
  }

  w(message: string, ...args: any[]): void {
    this.warn(message, ...args);
  }

  e(message: string, ...args: any[]): void {
    this.error(message, ...args);
  }

  f(message: string, ...args: any[]): void {
    this.fail(message, ...args);
  }

  getCategoryName(): string {
    return this.categoryName;
  }

  getCategoryType(): T {
    return new this.categoryType() as T;
  }
}

export class Log {
  /**
   * Static factory method to create loggers without dependency injection
   * Usage: const logger = Log.createFor(MyComponent);
   */
  static create<T>(categoryType: Constructor<T>): IShortLogger<T> {
    return new Logger<T>(categoryType);
  }

  private static formatMessage(level: LogLevel, message: string, category: string): string {
    const format = '[{timestamp}] ({level}) {{{category}}}: {message}';
    return format
      .replace('{timestamp}', new Date().toISOString())
      .replace('{level}', level)
      .replace('{{{category}}}', `{${category}}`)
      .replace('{message}', message);
  }

  private static getCategoryName(category: string | Constructor<any> | null | undefined): string {
    if (!category || category === '') return 'GLOBAL';
    if (typeof category === 'string') return category;
    return category.name || 'Anonymous';
  }

  // Static convenience methods with category as first parameter
  static d(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    const categoryName = Log.getCategoryName(category);
    const formattedMessage = Log.formatMessage('DBUG', message, categoryName);
    console.debug(formattedMessage, ...args);
  }

  static i(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    const categoryName = Log.getCategoryName(category);
    const formattedMessage = Log.formatMessage('INFO', message, categoryName);
    console.info(formattedMessage, ...args);
  }

  static w(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    const categoryName = Log.getCategoryName(category);
    const formattedMessage = Log.formatMessage('WARN', message, categoryName);
    console.warn(formattedMessage, ...args);
  }

  static e(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    const categoryName = Log.getCategoryName(category);
    const formattedMessage = Log.formatMessage('FAIL', message, categoryName);
    console.error(formattedMessage, ...args);
  }

  static f(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    const categoryName = Log.getCategoryName(category);
    const formattedMessage = Log.formatMessage('FAIL', message, categoryName);
    console.error(formattedMessage, ...args);
  }

  // Full method names
  static debug(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    Log.d(category, message, ...args);
  }

  static info(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    Log.i(category, message, ...args);
  }

  static warn(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    Log.w(category, message, ...args);
  }

  static error(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    Log.e(category, message, ...args);
  }

  static fail(category: string | Constructor<any> | null | undefined, message: string, ...args: any[]): void {
    Log.f(category, message, ...args);
  }
}
